const DB_NAME = 'engine-texture-lod-cache';
const STORE_NAME = 'images';
const DB_VERSION = 1;

/**
 * Caches low-resolution LOD textures using IndexedDB with an in-memory fallback.
 */
export class LodImageCache {
  private static instance: LodImageCache | null = null;

  private readonly memoryCache = new Map<string, string>();
  private readonly objectUrlRefs = new Map<string, number>();
  private readonly dbPromise: Promise<IDBDatabase | null>;

  private constructor() {
    this.dbPromise = this.openDatabase();
  }

  public static get Instance(): LodImageCache {
    if (!this.instance) {
      this.instance = new LodImageCache();
    }
    return this.instance;
  }

  /**
   * Returns a browser object URL for the given image URL.
   * The image is fetched and cached in IndexedDB (when available).
   */
  public async getObjectUrl(sourceUrl: string): Promise<string> {
    if (this.memoryCache.has(sourceUrl)) {
      return this.memoryCache.get(sourceUrl)!;
    }

    const db = await this.dbPromise;
    let blob: Blob | null = null;

    if (db) {
      blob = await this.readFromDb(db, sourceUrl);
    }

    if (!blob) {
      blob = await this.fetchBlob(sourceUrl);
      if (blob && db) {
        await this.writeToDb(db, sourceUrl, blob).catch(() => undefined);
      }
    }

    if (!blob) {
      throw new Error(`Failed to resolve preview image for ${sourceUrl}`);
    }

    const objectUrl = URL.createObjectURL(blob);
    this.memoryCache.set(sourceUrl, objectUrl);
    this.objectUrlRefs.set(objectUrl, (this.objectUrlRefs.get(objectUrl) ?? 0) + 1);
    return objectUrl;
  }

  /**
   * Releases an object URL reference (optional clean-up).
   */
  public release(objectUrl: string): void {
    const count = this.objectUrlRefs.get(objectUrl);
    if (count == null) return;
    if (count <= 1) {
      this.objectUrlRefs.delete(objectUrl);
      URL.revokeObjectURL(objectUrl);
    } else {
      this.objectUrlRefs.set(objectUrl, count - 1);
    }
  }

  private async fetchBlob(url: string): Promise<Blob | null> {
    try {
      const response = await fetch(url, { cache: 'force-cache' });
      if (!response.ok) {
        return null;
      }
      return await response.blob();
    } catch {
      return null;
    }
  }

  private async openDatabase(): Promise<IDBDatabase | null> {
    if (typeof indexedDB === 'undefined') {
      return null;
    }
    return new Promise((resolve) => {
      const request = indexedDB.open(DB_NAME, DB_VERSION);
      request.onerror = () => resolve(null);
      request.onupgradeneeded = () => {
        const db = request.result;
        if (!db.objectStoreNames.contains(STORE_NAME)) {
          db.createObjectStore(STORE_NAME, { keyPath: 'url' });
        }
      };
      request.onsuccess = () => resolve(request.result);
    });
  }

  private async readFromDb(db: IDBDatabase, url: string): Promise<Blob | null> {
    return new Promise((resolve) => {
      const transaction = db.transaction(STORE_NAME, 'readonly');
      const store = transaction.objectStore(STORE_NAME);
      const getRequest = store.get(url);
      getRequest.onerror = () => resolve(null);
      getRequest.onsuccess = () => {
        const result = getRequest.result;
        resolve(result?.blob ?? null);
      };
    });
  }

  private async writeToDb(db: IDBDatabase, url: string, blob: Blob): Promise<void> {
    return new Promise((resolve, reject) => {
      const transaction = db.transaction(STORE_NAME, 'readwrite');
      const store = transaction.objectStore(STORE_NAME);
      const putRequest = store.put({ url, blob, updatedAt: Date.now() });
      putRequest.onerror = () => reject(putRequest.error);
      putRequest.onsuccess = () => resolve();
    });
  }
}

